import { Shield, Clock, Award, Users } from 'lucide-react';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';

export const WhyChooseUs = () => {
  const benefits = [
    {
      icon: Shield,
      title: 'Fully Insured & Licensed',
      description: 'Complete cargo insurance and proper licensing ensure your shipments are protected every step of the way.',
      stat: '$2M',
      statLabel: 'Coverage'
    },
    {
      icon: Clock,
      title: 'On-Time Guarantee',
      description: 'We maintain a 99.5% on-time delivery rate with real-time tracking and proactive communication.',
      stat: '99.5%',
      statLabel: 'On-Time'
    },
    {
      icon: Award,
      title: 'Industry Experience',
      description: 'Over 15 years of logistics expertise serving businesses across GTA and Ontario with proven results.',
      stat: '15+',
      statLabel: 'Years'
    },
    {
      icon: Users,
      title: 'Dedicated Support',
      description: '24/7 customer support team ready to assist with your delivery needs, tracking, and any questions.',
      stat: '24/7',
      statLabel: 'Support'
    }
  ];

  return (
    <section className="py-24 relative overflow-hidden">
      {/* Background elements */}
      <div className="absolute top-0 right-0 w-[500px] h-[500px] bg-secondary/5 blur-[120px] rounded-full" />
      
      <div className="container mx-auto px-4 relative z-10">
        <div className="text-center mb-16 scroll-animate">
          <Badge variant="outline" className="mb-4 border-primary/50 text-primary bg-primary/10">
            Why Choose Us
          </Badge>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold mb-4">
            Trusted by <span className="text-gradient">Ontario Businesses</span>
          </h2>
          <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
            Experience the difference of working with a logistics partner that puts your business first.
          </p>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 mb-16">
          {benefits.map((benefit, index) => {
            const Icon = benefit.icon;
            return (
              <Card 
                key={index} 
                className="scroll-animate p-6 bg-card/50 backdrop-blur-sm border-border/50 hover:border-primary/50 transition-all duration-300 hover:shadow-glow group flex flex-col"
                style={{ animationDelay: `${index * 0.1}s` }}
              >
                <div className="mb-4">
                  <div className="p-3 rounded-xl bg-gradient-primary inline-flex group-hover:shadow-glow transition-all duration-300">
                    <Icon className="h-6 w-6 text-primary-foreground" />
                  </div>
                </div>
                
                <h3 className="text-xl font-semibold mb-2 group-hover:text-primary transition-colors">
                  {benefit.title}
                </h3>
                
                <p className="text-sm text-muted-foreground mb-4 flex-grow">
                  {benefit.description}
                </p>
                
                <div className="pt-4 border-t border-border/50 mt-auto">
                  <div className="text-3xl font-bold text-gradient">{benefit.stat}</div>
                  <div className="text-sm text-muted-foreground">{benefit.statLabel}</div>
                </div>
              </Card>
            );
          })}
        </div>

        {/* Image showcase */}
        <div className="scroll-animate">
          <Card className="overflow-hidden bg-card/50 backdrop-blur-sm border-border/50">
            <div className="grid md:grid-cols-2 gap-0">
              <div className="relative h-64 md:h-auto">
                <img 
                  src="https://images.unsplash.com/photo-1645736315000-6f788915923b?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2MDV8MHwxfHNlYXJjaHw0fHx3YXJlaG91c2UlMjBsb2dpc3RpY3N8ZW58MHx8fHwxNzc4MTA0MDAxfDA&ixlib=rb-4.1.0&q=85"
                  alt="Warehouse Operations"
                  className="w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-r from-transparent to-card/50" />
              </div>
              <div className="p-8 md:p-12 flex flex-col justify-center">
                <Badge variant="outline" className="mb-4 border-primary/50 text-primary bg-primary/10 w-fit">
                  Our Commitment
                </Badge>
                <h3 className="text-2xl md:text-3xl font-bold mb-4">
                  Professional Logistics You Can <span className="text-gradient">Rely On</span>
                </h3>
                <p className="text-muted-foreground mb-6 leading-relaxed">
                  At Syncline Logistics, we understand that your shipments represent more than just cargo—they're your business, your reputation, and your commitment to your customers. That's why we treat every delivery with the same care and urgency we'd want for our own business.
                </p>
                <div className="space-y-3">
                  <div className="flex items-start gap-3">
                    <div className="h-6 w-6 rounded-full bg-primary/20 flex items-center justify-center flex-shrink-0 mt-0.5">
                      <div className="h-2 w-2 rounded-full bg-primary" />
                    </div>
                    <div>
                      <div className="font-semibold">Professional Fleet</div>
                      <div className="text-sm text-muted-foreground">Modern, well-maintained vehicles</div>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="h-6 w-6 rounded-full bg-primary/20 flex items-center justify-center flex-shrink-0 mt-0.5">
                      <div className="h-2 w-2 rounded-full bg-primary" />
                    </div>
                    <div>
                      <div className="font-semibold">Experienced Drivers</div>
                      <div className="text-sm text-muted-foreground">Trained, vetted professionals</div>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="h-6 w-6 rounded-full bg-primary/20 flex items-center justify-center flex-shrink-0 mt-0.5">
                      <div className="h-2 w-2 rounded-full bg-primary" />
                    </div>
                    <div>
                      <div className="font-semibold">Advanced Tracking</div>
                      <div className="text-sm text-muted-foreground">Real-time shipment visibility</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </Card>
        </div>
      </div>
    </section>
  );
};

export default WhyChooseUs;
